import { useEffect } from "react";
import { categoryLabel } from "./catalog.ts";
import { haptic } from "./telegram.ts";

const ALL = "all";

/** A single-select group of filter chips (with an "All" reset chip). */
function FilterGroup({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="filter-group">
      <div className="filter-group-label">{label}</div>
      <div className="chips">
        <button
          className={`chip ${value === ALL ? "chip-active" : ""}`}
          onClick={() => {
            haptic("light");
            onChange(ALL);
          }}
        >
          All
        </button>
        {options.map((o) => (
          <button
            key={o.value}
            className={`chip ${value === o.value ? "chip-active" : ""}`}
            onClick={() => {
              haptic("light");
              onChange(value === o.value ? ALL : o.value);
            }}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Filter bottom-sheet. Replaces the old category/brand chip rows that cluttered
 * the catalog header as new groups were added: one "Filter" button opens this,
 * where the shopper narrows by category, brand, flavor, and strength (plus a
 * favorites-only toggle) in a single place. Selections update the catalog live;
 * the footer shows the resulting count and clears all filters.
 */
export function FilterSheet({
  categories,
  brands,
  flavors,
  strengths,
  category,
  brand,
  flavor,
  strength,
  favOnly,
  hasFavorites,
  resultCount,
  onCategory,
  onBrand,
  onFlavor,
  onStrength,
  onToggleFavOnly,
  onClear,
  onClose,
}: {
  categories: string[];
  brands: string[];
  flavors: string[];
  strengths: number[];
  category: string;
  brand: string;
  flavor: string;
  strength: string;
  favOnly: boolean;
  hasFavorites: boolean;
  resultCount: number;
  onCategory: (v: string) => void;
  onBrand: (v: string) => void;
  onFlavor: (v: string) => void;
  onStrength: (v: string) => void;
  onToggleFavOnly: () => void;
  onClear: () => void;
  onClose: () => void;
}) {
  // Lock body scroll while open; restore on close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const anyActive =
    category !== ALL ||
    brand !== ALL ||
    flavor !== ALL ||
    strength !== ALL ||
    favOnly;

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        className="sheet filter-sheet"
        role="dialog"
        aria-label="Filters"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle" onClick={onClose} />
        <div className="filter-body">
          <div className="filter-head">
            <h2 className="sheet-name">Filters</h2>
            {anyActive && (
              <button className="filter-clear" onClick={onClear}>
                Clear all
              </button>
            )}
          </div>

          {hasFavorites && (
            <div className="filter-group">
              <div className="chips">
                <button
                  className={`chip ${favOnly ? "chip-active" : ""}`}
                  onClick={() => {
                    haptic("light");
                    onToggleFavOnly();
                  }}
                >
                  ♥ Favorites only
                </button>
              </div>
            </div>
          )}

          <FilterGroup
            label="Category"
            options={categories.map((c) => ({ value: c, label: categoryLabel(c) }))}
            value={category}
            onChange={onCategory}
          />
          <FilterGroup
            label="Brand"
            options={brands.map((b) => ({ value: b, label: b }))}
            value={brand}
            onChange={onBrand}
          />
          <FilterGroup
            label="Flavor"
            options={flavors.map((f) => ({ value: f, label: f }))}
            value={flavor}
            onChange={onFlavor}
          />
          <FilterGroup
            label="Strength"
            options={strengths.map((s) => ({
              value: String(s),
              label: `${s} mg`,
            }))}
            value={strength}
            onChange={onStrength}
          />
        </div>

        <div className="filter-actions">
          <button className="btn" onClick={onClose}>
            {resultCount === 0
              ? "No matches"
              : `Show ${resultCount} product${resultCount === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
